// src/pages/Appli/AppliAdmin/ListingClients/hooks/useAssociations.js

import {
  useCallback,
  useEffect,
  useState,
} from "react";

import {
  deleteAssociation as deleteAssociationApi,
  getAllAssociations,
  patchAssociationFields,
  saveAssociation as saveAssociationApi,
  updateAssociation as updateAssociationApi,
} from "../../../../../services/associationsServices";

function mergeAssociation(current, updated) {
  return {
    ...current,
    ...updated,
    prospects:
      updated?.prospects ?? current?.prospects ?? [],
  };
}

export default function useAssociations() {
  const [associations, setAssociations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [updatingStatusIds, setUpdatingStatusIds] =
    useState([]);

  const loadAssociations = useCallback(async () => {
    setLoading(true);
    setLoadError(null);

    try {
      const data = await getAllAssociations();

      setAssociations(
        Array.isArray(data) ? data : [],
      );
    } catch (error) {
      setLoadError(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAssociations();
  }, [loadAssociations]);

  const createAssociation = useCallback(
    async (payload) => {
      setSaving(true);

      try {
        const created =
          await saveAssociationApi(payload);

        const normalized = {
          ...created,
          prospects: created?.prospects || [],
        };

        setAssociations((current) => [
          normalized,
          ...current,
        ]);

        return normalized;
      } finally {
        setSaving(false);
      }
    },
    [],
  );

  const editAssociation = useCallback(
    async (associationId, payload) => {
      setSaving(true);

      try {
        const updated =
          await updateAssociationApi(
            associationId,
            payload,
          );

        setAssociations((current) =>
          current.map((association) =>
            association.id === associationId
              ? mergeAssociation(
                  association,
                  updated,
                )
              : association,
          ),
        );

        return updated;
      } finally {
        setSaving(false);
      }
    },
    [],
  );

  const removeAssociation = useCallback(
    async (associationId) => {
      setDeletingId(associationId);

      try {
        const result =
          await deleteAssociationApi(associationId);

        setAssociations((current) =>
          current.filter(
            (association) =>
              association.id !== associationId,
          ),
        );

        return result;
      } finally {
        setDeletingId(null);
      }
    },
    [],
  );

  const changeAssociationStatus = useCallback(
    async (association, newStatus) => {
      if (
        !association ||
        !newStatus ||
        association.statut_asso === newStatus
      ) {
        return association;
      }

      const associationId = association.id;
      const previousStatus =
        association.statut_asso;

      setUpdatingStatusIds((current) => [
        ...new Set([...current, associationId]),
      ]);

      setAssociations((current) =>
        current.map((item) =>
          item.id === associationId
            ? {
                ...item,
                statut_asso: newStatus,
              }
            : item,
        ),
      );

      try {
        const updated =
          await patchAssociationFields(
            associationId,
            {
              statut_asso: newStatus,
            },
          );

        setAssociations((current) =>
          current.map((item) =>
            item.id === associationId
              ? mergeAssociation(item, updated)
              : item,
          ),
        );

        return updated;
      } catch (error) {
        setAssociations((current) =>
          current.map((item) =>
            item.id === associationId
              ? {
                  ...item,
                  statut_asso: previousStatus,
                }
              : item,
          ),
        );

        throw error;
      } finally {
        setUpdatingStatusIds((current) =>
          current.filter(
            (id) => id !== associationId,
          ),
        );
      }
    },
    [],
  );

  return {
    associations,
    loading,
    loadError,
    saving,
    deletingId,
    updatingStatusIds,

    loadAssociations,
    createAssociation,
    editAssociation,
    removeAssociation,
    changeAssociationStatus,
  };
}